

























































































































































































































































































































































































































































































































































































































































































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { State, Action, Getter } from "vuex-class";

import { endpoints } from "../../endpoints";
import buildUrl from "build-url";

import { DatasetState } from "@/store/datasets/types";

import SCEMap from "./Map.vue";
import Metrics from "./Metrics.vue";
import MetricsHistogram from "./MetricsHistogram.vue";

import { eventLoading } from "@/components/sce/EventLoading"; // This class controls loading state to update progress bar

@Component({
    components: {
        SCEMap,
        Metrics,
        MetricsHistogram,
    },
})
export default class SupplyChainExplorer extends Vue {
    @State("datasets") datasets!: DatasetState;
    @Action("datasets/getDatasets") getDatasets: any;

    // Display Settings.
    commodity: string = "dairy_product";
    metric: string = "tonnes";
    spatial_type: string = "lga";
    border_crossings: number | null = null;
    applied_border_crossings: number | null = null;
    spatial_selection: string[] = [];
    applied_spatial_selection: string[] = [];
    supply_chain_leg: string = "production_imports";
    applied_supply_chain_leg: string = "production_imports";
    loading: boolean = false; // Controls progress bar

    /** Short descriptions shown in the `?`-icon tooltips next to each sidebar filter.*/
    filterHelp = {
        commodity:
            "The freight commodity to analyse. The dashboard updates to show " +
            "only movements of this commodity and any sub-commodities listed " +
            "below the selector.",
        metric:
            "The performance measure used to " +
            "colour the maps. Switching the metric updates the shared " +
            "colour scale across all four maps.",
        region:
            "Geographic granularity for the maps. LGAs (Local Government " +
            "Areas) show fine-grained patterns; States or Territories show " +
            "broader regional trends.",
        crossings:
            "Filter to trips by how many state/territory borders they cross " +
            "en route — 0 means intrastate, 1 means neighbouring states, " +
            "2 or 3 mean longer interstate routes.",
        spatial:
            "Select one or more regions to narrow the analysis. The Supply " +
            "Chain Leg radio below determines whether this filter applies " +
            "to trip origins or destinations.",
        leg:
            "Choose which leg of the supply chain (Production & Imports, " +
            "Processing, Distribution, or Consumption & Exports) the region " +
            "filter applies to. Disabled until you select a region above.",
        colourScale:
            "All four maps use the same colour scale. The scale is applied " +
            "consistently across all maps, allowing values to be viewed and compared using a common range.",
    };

    /** Static notes for the "How to read this dashboard" explainer panel under the page title. */
    dashboardNotes = [
        {
            body:
                "This dashboard shows the supply chain for the selected commodity and filters. " +
                "This includes movements that indirectly supply or are supplied by the filtered movements. " +
                "End-to-end supply chain metrics provide summarised information about the typical journey of a commodity " +
                "taken from the beginning (i.e. point of production or import) to end (point of consumption or export) of a supply chain. " +
                "These supply chains typically consist of multiple legs, such as point of production to processor; " +
                "processor to distribution centre; distribution centre to market. " +
                "For each category of supply chain leg, summary metrics and maps at an LGA or state/territory level are shown. " +
                "For more information and worked examples, refer to the ",
            // Optional clickable link rendered inline at the end of
            // the body (see template).
            linkName: "user_guide_trasce",
            linkText: "Supply Chain Explorer User Guide",
        },
    ];

    /** True once the first batch of dashboard data has finished loading. false → show
     *  skeleton placeholders, true → show real content. */
    hasLoadedOnce: boolean = false;

    isDirty: boolean = false;

    histogramVisible = false;
    selectedMetricTitle = "";
    selectedMetric: any = null;

    /** Parent's record of each child map's data extent — used to compute a shared colour scale across all 4 maps. */
    mapDataRanges: Record<string, { min: number | null; max: number | null }> =
        {};

    get dataset(): any {
        return this.datasets.dataset;
    }

    get spatialBorderCrossing(): boolean {
        return this.border_crossings !== null;
    }

    //-----------------------------
    //--- Visualization Options ---
    //-----------------------------

    get commodityList() {
        var list = Object.keys(this.commodity_items);

        var ret = list.map((value) => {
            // Replace underscores with spaces
            var spaced = value.replace(/_/g, " ");

            // Capitalize each word
            var text = spaced
                .split(" ")
                .map((value) => value.charAt(0).toUpperCase() + value.slice(1))
                .join(" ");
            return { text: text, value: value };
        });

        // Sort alphabetically by text
        return ret.sort((a, b) => a.text.localeCompare(b.text));
    }

    get includedCommodities() {
        var commodities =
            this.commodity_items[this.commodity.toLowerCase()]?.map((c: any) =>
                c.replace(/_/g, " "),
            ) || [];
        return commodities.join(", ");
    }

    // The available metric items that can be reported upon.
    metric_items = {
        tonnes: {
            text: "Percent of total tonnes",
            value: "tonnes",
            valueformat: ",.4s",
            valuesuffix: " t",
            units: "(tonne)",
            tickprefixformat: "",
            customDisabled: false,
        },
        node: {
            text: "Number of enterprises",
            value: "node",
            valueformat: ",.4s",
            valuesuffix: "",
            units: "(ent)",
            tickprefixformat: "",
            customDisabled: false,
        },
        lga: {
            text: "Number of Local Government Areas",
            value: "lga",
            valueformat: ",.4s",
            valuesuffix: "",
            units: "(lga)",
            tickprefixformat: "",
            customDisabled: false,
        },
        state: {
            text: "Number of States and Territories",
            value: "state",
            valueformat: ",.4s",
            valuesuffix: "",
            units: "(st/terr)",
            tickprefixformat: "",
            customDisabled: false,
        },
    };

    // The spatial regions which can be visualized on the map.
    spatialTypeItems = {
        lga: {
            text: "Local Government Areas",
            value: "lga",
            geojson: "./docs/lga.json",
        },
        state: {
            text: "States or Territories",
            value: "state",
            geojson: "./docs/states.json",
        },
    };

    // Options loaded via endpoints.
    commodity_items: any = {};
    lga_items: string[] = [];
    state_items: string[] = [];

    // The available selection items for selecting the number of border crossings.
    border_crossings_items = [
        { value: 0, text: "0 Crossings (i.e. Intrastate)" },
        { value: 1, text: "1 Crossing (i.e. Neighbouring)" },
        { value: 2, text: "2 Crossings" },
        { value: 3, text: "3 Crossings" },
    ];

    // This is only temporary...
    get commodityGroupsUrl() {
        return (
            process.env.VUE_APP_BACKEND_BASE +
            "/supplychainexplorer/commoditygroups"
        );
    }

    mounted() {
        // Load the list of commodities.
        Vue.axios({
            url: this.commodityGroupsUrl,
        }).then(
            (response: any) => {
                this.commodity_items = response.data;
            },
            (error: any) => {
                console.log(error);
            },
        );

        // Load the boundary datasets.
        if (this.dataset) {
            this.onDatasetChanged();
        }
    }

    @Watch("dataset", { deep: true })
    onDatasetChanged() {
        // Load the list of LGAs.
        Vue.axios({
            url: buildUrl(endpoints.boundariesUrl(this.dataset), {
                queryParams: { layer: "LGA" },
            }),
        }).then(
            (response: any) => {
                this.lga_items = response.data;
            },
            (error: any) => {
                console.log(error);
            },
        );

        // Load the list of States & Territories.
        Vue.axios({
            url: buildUrl(endpoints.boundariesUrl(this.dataset), {
                queryParams: { layer: "STE11a" },
            }),
        }).then(
            (response: any) => {
                this.state_items = response.data;
            },
            (error: any) => {
                console.log(error);
            },
        );
    }

    created() {
        this.getDatasets();
        eventLoading.$on("loading", this.setLoading);
    }

    beforeDestroy() {
        eventLoading.$off("loading", this.setLoading);
    }

    setLoading(isLoading: boolean) {
        // First time loading transitions back to false marks the end
        // of the initial dashboard load — flip `hasLoadedOnce` so the
        // skeleton can fade out and the real content can fade in.
        if (!isLoading && this.loading && !this.hasLoadedOnce) {
            this.hasLoadedOnce = true;
        }
        this.loading = isLoading;
    }

    /** Drives the main content's interactivity. Hidden until the first load completes
     *  (skeleton shows instead). Pointer events stay off until loading finishes. */
    get mainContentStateClass(): string {
        if (!this.hasLoadedOnce) return "main-content--hidden";
        return this.loading ? "main-content--loading" : "";
    }

    @Watch("supply_chain_leg")
    @Watch("border_crossings")
    @Watch("spatial_selection")
    onSpatialParamsChanged() {
        if (this.spatial_selection.length === 0) {
            this.applyFilters();
            return;
        }
        this.isDirty = true;
    }

    clearSpatialSelection_all() {
        this.onSpatialSelectionUpdated([]);
        this.applied_spatial_selection = [];
        this.supply_chain_leg = "production_imports";
        this.applied_supply_chain_leg = "production_imports";
        this.border_crossings = null;
        this.applied_border_crossings = null;
    }

    updateSpatialParams(e: any) {
        var location = e.points[0].location;
        var newLeg = e.points[0].customdata[1];

        var nextSelection = this.spatial_selection;

        // If switching maps, reset selection silently
        if (this.supply_chain_leg !== newLeg) {
            nextSelection = [];
        }

        // Update leg
        this.supply_chain_leg = newLeg;

        // Toggle logic
        if (nextSelection.includes(location)) {
            nextSelection = nextSelection.filter((l) => l !== location);
        } else {
            nextSelection = [...nextSelection, location];
        }

        // Update spatial selection
        this.onSpatialSelectionUpdated(nextSelection);
    }

    //-----------------------------
    //-------      Data       -----
    //-----------------------------

    // Configuration for the 4 supply chain leg maps.
    densitymaps = {
        production_imports: {
            map_id: "production_imports",
            title: "Production and Imports",
            hint: "Production and Imports supply chain leg includes movements originating from enterprises where commodities are produced or imported. E.g. properties where fruit is grown or ports where commodities are imported. Selecting a region displayed on the map adds that region to the spatial filter. ",
            direction: "from", //orig
        },
        processing: {
            map_id: "processing",
            title: "Processing",
            hint: "Processing supply chain leg includes movements originating from enterprises where commodities are processed. This involves transformations from incoming commodities to outgoing commodities. E.g. apples to fruit juice. Selecting a region displayed on the map adds that region to the spatial filter. ",
            direction: "from", //orig
        },
        distribution: {
            map_id: "distribution",
            title: "Distribution",
            hint: "Distribution supply chain leg includes movements originating from enterprises where distribution of freight occurs. E.g. intermodals and distribution centers. Selecting a region displayed on the map adds that region to the spatial filter. ",
            direction: "from", //orig
        },
        consumption_exports: {
            map_id: "consumption_exports",
            title: "Consumption and Exports",
            hint: "Consumption and Exports supply chain leg includes movements going to end-user enterprises or ports. E.g. commodities moving to supermarkets or ports for export. Selecting a region displayed on the map adds that region to the spatial filter. ",
            direction: "to", //dest
        },
    };

    onMetricClicked(title: string, metric: any) {
        this.selectedMetricTitle = title;
        this.selectedMetric = metric;
        this.histogramVisible = true;
    }

    /** Receive each SCEMap's raw data range. Replacing the whole object
     *  rather than mutating in place because Vue 2 doesn't pick up
     *  property additions on plain objects without `$set`, and the
     *  shallow-spread is simpler than threading $set through here. */
    onMapDataRange(payload: {
        map_id: string;
        min: number | null;
        max: number | null;
    }) {
        this.mapDataRanges = {
            ...this.mapDataRanges,
            [payload.map_id]: { min: payload.min, max: payload.max },
        };
    }

    /** Clear stale per-map ranges whenever the metric switches so each
     *  child reports a fresh range under the new metric before the
     *  shared scale is recomputed. Without this the aggregate would
     *  briefly include values from the previous metric. */
    @Watch("metric")
    onMetricSwitched() {
        this.mapDataRanges = {};
    }

    /** Aggregate min / max across all four child maps' currently-reported
     *  ranges. Used both for the shared legend's end labels and as the
     *  `zmin`/`zmax` passed back to each SCEMap so all four use the same
     *  colour-to-value mapping. */
    get globalDataRange(): { min: number; max: number } | null {
        let min = Infinity;
        let max = -Infinity;
        let any = false;
        for (const r of Object.values(this.mapDataRanges)) {
            if (r.min == null || r.max == null) continue;
            if (!Number.isFinite(r.min) || !Number.isFinite(r.max)) continue;
            if (r.min < min) min = r.min;
            if (r.max > max) max = r.max;
            any = true;
        }
        return any ? { min, max } : null;
    }

    /** Labels shown at the ends of the shared colour-ramp legend bar.
     *  Percent metric uses a fixed log scale (zmin = -4, zmax = 0 in
     *  SCEMap), so the endpoint labels are hard-coded; the count
     *  metrics read from `globalDataRange` once available. */
    get sharedLegendMinMax(): { min: string; max: string } {
        const m = (this.metric_items as any)[this.metric];
        if (m.value === "tonnes") return { min: "0.01%", max: "100%" };
        if (!this.globalDataRange) return { min: "", max: "" };
        const suffix = m.valuesuffix || "";
        return {
            min: `${this.formatLegendNumber(
                this.globalDataRange.min,
            )}${suffix}`,
            max: `${this.formatLegendNumber(
                this.globalDataRange.max,
            )}${suffix}`,
        };
    }

    formatLegendNumber(n: number): string {
        if (!Number.isFinite(n)) return "";
        if (n >= 1000) {
            return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
        if (n >= 10) return n.toFixed(0);
        return n.toFixed(1);
    }

    onCrossingSelected(payload: { clickedValue: number; map_id: string }) {
        this.border_crossings = payload.clickedValue;
        this.supply_chain_leg = payload.map_id;
    }

    onSpatialSelectionUpdated(newSelection: string[]) {
        this.spatial_selection = newSelection || [];
    }

    applyFilters() {
        this.applied_spatial_selection = [...this.spatial_selection];
        this.applied_supply_chain_leg = this.supply_chain_leg;
        this.applied_border_crossings = this.border_crossings;
        this.isDirty = false;
    }

    user_guide(name: any) {
        (window as any).open("./docs/" + name + ".pdf");
    }
}
